import type { FC } from 'react';

import React from 'react';
import type { OnwheelPricing } from '@/api/cms/onwheel-pricing/types';
import type { Banner, DefaultCmsDataResponse } from '@/api/cms/types';
import BannerSwiper from '@/components/BannerSwiper';
import Button from '@/components/Button';
import Container from '@/components/layouts/Container';
import { baseUrl } from '@/lib/constants';
import MeetAllDemands from '@/modules/customer/cooperate/MeetAllDemands';
import HelpFormContent from '@/modules/customer/HelpFormContent';
import BenefitsContent from '../../BenefitsContent';
import { onWheelBenefitsData, onwheelFeaturesData } from '../cooperateData';
import CooperateServiceOnWheelForm from '../onwheel/CooperateServiceOnwheelForm';
import OnWheelCustomers from './OnWheelCustomers';
import OnWheelPricing from './OnWheelPricing';

type Props = {
  // Declare OnWheelPage props
  onwheelPricingData: DefaultCmsDataResponse<OnwheelPricing> | null;
  onwheelBannerData: DefaultCmsDataResponse<Banner> | null;
};

export const OnWheelPage: FC<Props> = ({
  onwheelPricingData,
  onwheelBannerData,
}: Props) => {
  const title = 'OnWheel - Phần mềm Quản lý giao hàng chuyên nghiệp';
  const bannerImgUrl = React.useMemo(() => {
    let bannerUrl = undefined;
    try {
      bannerUrl =
        onwheelBannerData?.attributes.items[0].desktop_image.data.attributes
          .url;
    } catch (error) {
      bannerUrl = undefined;
    }

    return bannerUrl;
  }, [onwheelBannerData]);

  return (
    <Container
      title={title}
      description="OnWheel - Phần mềm Quản lý giao hàng chuyên nghiệp: 2 trong 1, Tính năng nâng cao và tùy biến theo nhu cầu, Hạ tầng bản đố số hàng đầu Việt Nam"
      openGraph={{
        type: 'website',
        site_name: process.env.NEXT_PUBLIC_APP_NAME,
        title,
        images: [
          {
            url:
              bannerImgUrl ||
              `${baseUrl}/static/images/home/banner-faster-delivery-desktop.webp`,
            height: bannerImgUrl ? 562.5 : 627,
            width: 1200,
            alt: title || process.env.NEXT_PUBLIC_APP_NAME,
          },
        ],
      }}
    >
      <main className="mt-header-mobile desktop:mt-header-desktop min-h-content flex w-full grow flex-col">
        <BannerSwiper bannerData={onwheelBannerData} />
        <BenefitsContent
          title="Giải pháp OnWheel"
          predesc="Phần mềm quản lý đơn hàng chuyên nghiệp"
          desc="mang đến cho bạn công nghệ đang quản lý đội ngũ tài xế đông đảo và chuyên nghiệp để vận chuyển đơn hàng của bạn một cách dễ dàng và nhanh chóng."
          items={onWheelBenefitsData}
          type="benefit"
          className="bg-white"
          contentClassName="desktop:grid-cols-2 max-w-5xl mx-auto"
        >
          <Button
            type={'link'}
            href="https://blog.onwheel.io/"
            setLocale={false}
          >
            Xem thêm các bài viết
          </Button>
        </BenefitsContent>
        <MeetAllDemands
          title="Tính năng nổi bật"
          desc="Sự ưu việt của sản phẩm đã được kiểm chứng với nhiều mô hình kinh doanh như TMĐT, F&B, xe tải..v..v... trong hơn 7 năm qua."
          items={onwheelFeaturesData}
          button="Xem tài liệu"
          moreInfo="Xem API"
          hasMoreInfo={true}
          hrefButton="https://documentation.onwheel.io/docs/tai-lieu/1-cong-cu-quan-ly/"
          hrefMoreInfo="https://developers.ahamove.com/?_ga=2.51888745.1762548968.1666703386-1538391770.1657635142#overview"
          setLocaleButton={false}
          setLocaleMoreInfo={false}
        />
        <OnWheelCustomers />
        {onwheelPricingData ? (
          <OnWheelPricing onwheelPricingData={onwheelPricingData} />
        ) : null}
        <HelpFormContent
          form={
            <CooperateServiceOnWheelForm type="service" service="onwheel" />
          }
          id="register-form"
        />
      </main>
    </Container>
  );
};
